import { MAX_CANVAS_PIXELS, rasterPlan } from './plan';

/*
 * PNG/JPG download options (หน้าส่งออก, choices 1ก–4ก), free of DOM/Fabric for the tests (tests/export-options.test.ts):
 *   ขนาด          a multiplier ×0.25 – ×3 of the page's own size, shown in px; past what an iPad can draw it is made
 *                 smaller and the sheet says so in red (never silently)
 *   คุณภาพ        JPG ต่ำ / ปานกลาง / สูง / จำกัดขนาดไฟล์ · PNG สูง / จำกัดขนาดไฟล์
 *   พื้นหลังโปร่งใส  PNG only: the page colour is not drawn
 *   บันทึกการตั้งค่า   kept in this device's browser (4ก), read back with every value checked
 */

export const MULT_MIN = 0.25;
export const MULT_MAX = 3;

export type JpgQuality = 'low' | 'medium' | 'high' | 'limit';
export type PngQuality = 'high' | 'limit';
export const JPG_QUALITY_LABEL: Record<JpgQuality, string> = { low: 'ต่ำ', medium: 'ปานกลาง', high: 'สูง', limit: 'จำกัดขนาดไฟล์' };
export const PNG_QUALITY_LABEL: Record<PngQuality, string> = { high: 'สูง', limit: 'จำกัดขนาดไฟล์' };
/** the browser's JPEG quality for each choice ("สูง" is what every JPG was before) */
export const JPG_Q: Record<Exclude<JpgQuality, 'limit'>, number> = { low: 0.6, medium: 0.8, high: 0.92 };

export const LIMIT_KB_MIN = 20;
export const LIMIT_KB_MAX = 50_000;

/** what is remembered: the file type too, so the sheet opens as it was left */
export interface ExportSettings {
  format: 'png' | 'jpg' | 'pdf' | 'doc' | 'print';
  mult: number;
  jpgQuality: JpgQuality;
  pngQuality: PngQuality;
  limitKb: number;
  transparent: boolean;
  /** several files as one ZIP (เลือกหน้า 3ค) */
  zip: boolean;
  /** a PDF of several pages as one file a page (4ก) */
  splitPdf: boolean;
}
export const DEFAULT_SETTINGS: ExportSettings = {
  format: 'png', mult: 1, jpgQuality: 'high', pngQuality: 'high', limitKb: 500, transparent: false, zip: true, splitPdf: false
};
export const SETTINGS_KEY = 'craftstudio.exportSettings.v1';

/** a typed multiplier: the number, or why it cannot be used (the download button waits until it can) */
export function checkMult(text: string): { ok: true; value: number } | { ok: false; error: string } {
  const v = Number(String(text).trim().replace(',', '.'));
  if (!String(text).trim() || !Number.isFinite(v)) return { ok: false, error: 'ใส่ตัวคูณขนาดเป็นตัวเลข เช่น 1 หรือ 1.5' };
  if (v < MULT_MIN || v > MULT_MAX) return { ok: false, error: `ตัวคูณขนาดต้องอยู่ระหว่าง ${MULT_MIN} ถึง ${MULT_MAX}` };
  return { ok: true, value: Math.round(v * 100) / 100 };
}

/** a typed size limit in KB */
export function checkLimitKb(text: string): { ok: true; value: number } | { ok: false; error: string } {
  const v = Number(String(text).trim().replace(/,/g, ''));
  if (!String(text).trim() || !Number.isFinite(v)) return { ok: false, error: 'ใส่ขนาดไฟล์สูงสุดเป็นตัวเลข (KB) เช่น 500' };
  if (v < LIMIT_KB_MIN || v > LIMIT_KB_MAX) return { ok: false, error: `ขนาดไฟล์สูงสุดต้องอยู่ระหว่าง ${LIMIT_KB_MIN} ถึง ${LIMIT_KB_MAX.toLocaleString('en-US')} KB` };
  return { ok: true, value: Math.round(v) };
}

export interface SizePlan {
  /** what the multiplier asks for */
  wantW: number; wantH: number;
  /** what will be drawn */
  outW: number; outH: number;
  /** from the page's px to the file's px */
  scale: number;
  /** made smaller than asked: an iPad/iPhone cannot draw that many pixels */
  reduced: boolean;
}

/** the file size for a page `w` × `h` at multiplier `mult`, kept within what the device can draw */
export function sizePlan(w: number, h: number, mult: number, maxPixels = MAX_CANVAS_PIXELS): SizePlan {
  const wantW = Math.max(1, Math.round(w * mult)), wantH = Math.max(1, Math.round(h * mult));
  const p = rasterPlan(w * mult, h * mult, maxPixels);
  return { wantW, wantH, outW: Math.max(1, p.outW), outH: Math.max(1, p.outH), scale: mult * p.scale, reduced: p.reduced };
}

/** the stored settings, every value checked; anything unreadable gives the defaults and says so */
export function parseSettings(raw: string | null): { settings: ExportSettings; problem?: string } {
  if (raw == null) return { settings: { ...DEFAULT_SETTINGS } };
  let v: Record<string, unknown>;
  try { v = JSON.parse(raw) as Record<string, unknown>; }
  catch { return { settings: { ...DEFAULT_SETTINGS }, problem: 'การตั้งค่าที่บันทึกไว้อ่านไม่ได้ จึงใช้ค่าเริ่มต้น' }; }
  if (!v || typeof v !== 'object') return { settings: { ...DEFAULT_SETTINGS }, problem: 'การตั้งค่าที่บันทึกไว้อ่านไม่ได้ จึงใช้ค่าเริ่มต้น' };
  const s = { ...DEFAULT_SETTINGS };
  const bad: string[] = [];
  if (['png', 'jpg', 'pdf', 'doc', 'print'].includes(v.format as string)) s.format = v.format as ExportSettings['format']; else bad.push('ชนิดไฟล์');
  const m = checkMult(String(v.mult)); if (m.ok) s.mult = m.value; else bad.push('ขนาด');
  if ((v.jpgQuality as string) in JPG_QUALITY_LABEL) s.jpgQuality = v.jpgQuality as JpgQuality; else bad.push('คุณภาพ JPG');
  if ((v.pngQuality as string) in PNG_QUALITY_LABEL) s.pngQuality = v.pngQuality as PngQuality; else bad.push('คุณภาพ PNG');
  const k = checkLimitKb(String(v.limitKb)); if (k.ok) s.limitKb = k.value; else bad.push('ขนาดไฟล์สูงสุด');
  if (typeof v.transparent === 'boolean') s.transparent = v.transparent; else bad.push('พื้นหลังโปร่งใส');
  // added later: settings saved before them simply do not have them (no complaint)
  if (typeof v.zip === 'boolean') s.zip = v.zip; else if (v.zip !== undefined) bad.push('รวมเป็น ZIP');
  if (typeof v.splitPdf === 'boolean') s.splitPdf = v.splitPdf; else if (v.splitPdf !== undefined) bad.push('PDF ไฟล์แยก');
  return bad.length ? { settings: s, problem: `การตั้งค่าที่บันทึกไว้บางค่าใช้ไม่ได้ (${bad.join(', ')}) จึงใช้ค่าเริ่มต้นแทน` } : { settings: s };
}

/* ---------- จำกัดขนาดไฟล์ ---------- */

export interface FitResult<B> { blob: B; scale: number; quality: number | null }
/** smallest picture side tried before giving up (smaller is useless as a picture) */
export const FIT_MIN_SIDE = 200;

/**
 * The largest file that is not over `limitBytes`: a JPG first lowers its quality (down to 0.4), then its size;
 * a PNG (no quality) lowers only its size. `encode(k, q)` makes the file at k × the planned size (k ≤ 1) and
 * JPEG quality q. When even the smallest try is too big, it says why — never a file over the limit.
 */
export async function fitToBytes<B extends { size: number }>(
  encode: (k: number, q: number | null) => Promise<B>,
  o: { limitBytes: number; jpg: boolean; longSide: number }
): Promise<FitResult<B>> {
  const fits = (b: B) => b.size <= o.limitBytes;
  let k = 1, smallest: B | null = null;
  for (let round = 0; round < 8; round++) {
    if (o.longSide * k < FIT_MIN_SIDE) break;
    if (!o.jpg) {
      const b = await encode(k, null);
      if (fits(b)) return { blob: b, scale: k, quality: null };
      smallest = b;
      k *= Math.min(0.9, Math.sqrt(o.limitBytes / b.size) * 0.95);
      continue;
    }
    const top = await encode(k, 0.92);
    if (fits(top)) return { blob: top, scale: k, quality: 0.92 };
    const low = await encode(k, 0.4);
    if (fits(low)) {
      // the best quality that still fits, between 0.4 and 0.92
      let lo = 0.4, hi = 0.92, best = low, bestQ = 0.4;
      for (let i = 0; i < 6; i++) {
        const q = Math.round(((lo + hi) / 2) * 100) / 100;
        const b = await encode(k, q);
        if (fits(b)) { lo = q; best = b; bestQ = q; } else hi = q;
      }
      return { blob: best, scale: k, quality: bestQ };
    }
    smallest = low;
    k *= Math.min(0.9, Math.sqrt(o.limitBytes / low.size) * 0.95);
  }
  const kb = (n: number) => Math.ceil(n / 1024).toLocaleString('en-US');
  throw new Error(`ทำไฟล์ให้ไม่เกิน ${kb(o.limitBytes)} KB ไม่ได้${smallest ? ` (เล็กสุดที่ทำได้ประมาณ ${kb(smallest.size)} KB)` : ''} ลองเพิ่มขนาดไฟล์สูงสุด${o.jpg ? '' : ' หรือใช้ JPG ซึ่งไฟล์เล็กกว่า'}`);
}
